import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Loader2, ArrowRight, Ship, ArrowLeft } from "lucide-react";

const BG = "https://images.pexels.com/photos/9541392/pexels-photo-9541392.jpeg";

export default function Login() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setStep("otp");
      if (data.dev_code) {
        toast.info(`Dev mode: your code is ${data.dev_code}`, { duration: 15000 });
      } else {
        toast.success(data.message);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (value) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code: value });
      login(data.token, data.user);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      {/* Left visual */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Container port at dusk" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
          <div className="font-num text-xs uppercase tracking-[0.3em] text-white/80">
            wedelvr Logistics
          </div>
          <div>
            <h1 className="font-display text-5xl font-bold tracking-tight leading-[1.05]">
              Asia–Australia
              <br />
              Trade Briefing
            </h1>
            <p className="mt-4 text-white/70 max-w-md leading-relaxed">
              Ocean freight market intelligence — capacity, cost & reliability for Thailand-origin
              flows into Australia. Private, invite-only access.
            </p>
          </div>
          <div className="font-num text-xs text-white/80 [text-shadow:_0_1px_3px_rgb(0_0_0_/_55%)]">Aug – Oct 2026 · Prepared 16 Oct 2026</div>
        </div>
      </div>

      {/* Right auth card */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Ship className="w-5 h-5 text-primary" />
            <span className="font-display font-bold tracking-tight">Asia–Australia Briefing</span>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 shadow-2xl">
            {step === "email" ? (
              <form onSubmit={requestOtp}>
                <div className="font-num text-xs uppercase tracking-[0.2em] text-slate-500">Secure access</div>
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-2">Sign in to your briefing</h2>
                <p className="text-sm text-slate-500 mt-2 mb-6">
                  Enter your approved email. We'll send a one-time 6-digit code to verify it's you.
                </p>
                <label className="text-sm font-medium text-slate-700">Email address</label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  required
                  autoFocus
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 h-11"
                />
                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 mt-5 bg-primary hover:bg-primary/90"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send login code <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
                <p className="text-center text-xs text-slate-400 mt-6">
                  Administrator?{" "}
                  <Link to="/admin/login" className="text-primary hover:underline" data-testid="login-admin-link">
                    Admin sign in
                  </Link>
                </p>
              </form>
            ) : (
              <div>
                <button
                  onClick={() => { setStep("email"); setCode(""); }}
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
                  data-testid="login-back-button"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <h2 className="font-display text-2xl font-bold text-slate-900">Enter your code</h2>
                <p className="text-sm text-slate-500 mt-2 mb-6">
                  We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>. It expires in 10 minutes.
                </p>
                <div className="flex justify-center" data-testid="login-otp-input">
                  <InputOTP
                    maxLength={6}
                    value={code}
                    onChange={(v) => {
                      setCode(v);
                      if (v.length === 6) verifyOtp(v);
                    }}
                    disabled={loading}
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg font-num" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  data-testid="login-verify-button"
                  onClick={() => verifyOtp(code)}
                  disabled={loading || code.length !== 6}
                  className="w-full h-11 mt-6 bg-primary hover:bg-primary/90"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enter"}
                </Button>
                <button
                  onClick={requestOtp}
                  disabled={loading}
                  className="w-full text-center text-xs text-slate-500 hover:text-primary mt-4"
                  data-testid="login-resend-button"
                >
                  Didn't get it? Resend code
                </button>
              </div>
            )}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            Access is restricted to approved email addresses only.
          </p>
        </div>
      </div>
    </div>
  );
}
